import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useGenerateLessonSubtitlesMutation,
  useLazyGetLessonSubtitlesQuery,
} from "../../services/users/ai";
import { useAiPoll } from "../../hooks/useAiPoll";
import { toastAiError } from "../../utils/aiErrors";

const SubtitlesControls = ({
  lessonId,
  transcriptReady,
  transcriptStatus,
  transcriptGenerating,
  transcriptPolling,
  transcriptTimedOut,
  onGenerateTranscript,
  subtitleEnabled,
  onSubtitleEnabledChange,
  subtitleLanguage,
  onSubtitleLanguageChange,
  onSegmentsChange,
}) => {
  const { t } = useTranslation("ai");
  const [getSubtitles] = useLazyGetLessonSubtitlesQuery();
  const [generateSubtitles, { isLoading: isGenerating }] =
    useGenerateLessonSubtitlesMutation();

  const [status, setStatus] = useState("idle");
  const [shouldPoll, setShouldPoll] = useState(false);

  const loadLanguage = useCallback(
    async (lang) => {
      setStatus("loading");
      const result = await getSubtitles({ lessonId, language: lang });
      if (result.error) {
        if (result.error.status === 404) {
          setStatus("not_generated");
          onSegmentsChange?.(null);
          return;
        }
        toastAiError(result.error, t);
        setStatus("not_generated");
        return;
      }
      const data = result.data;
      if (data?.status === "completed") {
        setStatus("completed");
        onSegmentsChange?.(data.segments || []);
        setShouldPoll(false);
        return;
      }
      if (data?.status === "pending") {
        setStatus("pending");
        setShouldPoll(true);
        return;
      }
      setStatus("not_generated");
      onSegmentsChange?.(null);
    },
    [getSubtitles, lessonId, onSegmentsChange, t],
  );

  useEffect(() => {
    if (!transcriptReady) {
      setStatus("idle");
      onSegmentsChange?.(null);
      setShouldPoll(false);
      return;
    }
    loadLanguage(subtitleLanguage);
  }, [lessonId, transcriptReady, subtitleLanguage]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchForPoll = useCallback(async () => {
    const result = await getSubtitles({
      lessonId,
      language: subtitleLanguage,
    });
    if (result.error) {
      if (result.error.status === 404) return { status: "pending" };
      throw result.error;
    }
    return result.data;
  }, [getSubtitles, lessonId, subtitleLanguage]);

  const { isPolling, timedOut, start: startPoll } = useAiPoll(fetchForPoll, {
    enabled: shouldPoll,
    onCompleted: (data) => {
      setStatus("completed");
      onSegmentsChange?.(data.segments || []);
      setShouldPoll(false);
    },
    onError: (err) => {
      setShouldPoll(false);
      toastAiError(err, t);
      setStatus("not_generated");
    },
    onTimeout: () => setShouldPoll(false),
  });

  const handleGenerate = async () => {
    try {
      const result = await generateSubtitles(lessonId);
      if (result.error) {
        toastAiError(result.error, t);
        return;
      }
      setStatus("pending");
      setShouldPoll(true);
      startPoll();
    } catch (err) {
      toastAiError(err, t);
    }
  };

  if (!transcriptReady) {
    const isBusy =
      transcriptStatus === "loading" ||
      transcriptStatus === "pending" ||
      transcriptGenerating ||
      transcriptPolling;

    return (
      <div className="ai-subtitles-bar">
        <div className="ai-subtitles-bar__row">
          {isBusy ? (
            <>
              <p className="ai-muted mb-0">{t("transcript.generating")}</p>
              {transcriptTimedOut && (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={onGenerateTranscript}
                  disabled={transcriptGenerating}
                >
                  {t("transcript.retry")}
                </button>
              )}
            </>
          ) : (
            <>
              <p className="ai-muted mb-0">{t("subtitles.needTranscript")}</p>
              <button
                type="button"
                className="btn btn-main-color-border font-15"
                onClick={onGenerateTranscript}
                disabled={transcriptGenerating}
              >
                {t("transcript.generate")}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="ai-subtitles-bar">
      <div className="ai-subtitles-bar__row">
        <span className="ai-subtitles-bar__label">{t("subtitles.title")}</span>

        {(status === "pending" || isPolling || isGenerating) && (
          <span className="ai-muted">{t("subtitles.generating")}</span>
        )}

        {status === "not_generated" && (
          <button
            type="button"
            className="btn btn-main-color-border font-15"
            onClick={handleGenerate}
            disabled={isGenerating}
          >
            {t("subtitles.generate")}
          </button>
        )}

        {status === "completed" && (
          <>
            <label className="ai-toggle">
              <input
                type="checkbox"
                checked={subtitleEnabled}
                onChange={(e) => onSubtitleEnabledChange?.(e.target.checked)}
              />
              <span>
                {subtitleEnabled ? t("subtitles.show") : t("subtitles.hide")}
              </span>
            </label>
            <div
              className="ai-lang-toggle"
              role="group"
              aria-label={t("subtitles.language")}
            >
              <button
                type="button"
                className={`btn font-15 ${
                  subtitleLanguage === "en"
                    ? "btn-main-color"
                    : "btn-main-color-border"
                }`}
                onClick={() => onSubtitleLanguageChange?.("en")}
              >
                {t("subtitles.en")}
              </button>
              <button
                type="button"
                className={`btn font-15 ${
                  subtitleLanguage === "ar"
                    ? "btn-main-color"
                    : "btn-main-color-border"
                }`}
                onClick={() => onSubtitleLanguageChange?.("ar")}
              >
                {t("subtitles.ar")}
              </button>
            </div>
          </>
        )}

        {timedOut && (
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={startPoll}
          >
            {t("subtitles.retry")}
          </button>
        )}
      </div>
    </div>
  );
};

export default SubtitlesControls;
